import { useCallback, useEffect, useMemo, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentRailsService } from "../services/paymentRails.service";
import type { RailAvailabilityCell } from "../types/paymentRails.types";

export interface RailKpis {
  totalCells: number;
  disabledCells: number;
  cellsWithOverrides: number;
}

export interface UseRailSettingsResult {
  cells: RailAvailabilityCell[];
  kpis: RailKpis;
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

/**
 * State of the rail-availability page, scoped to one organization or to the global view (`organizationId`
 * null). Freshness: no polling — rail settings change by human action only; data reloads on the manual button
 * and after every successful mutation.
 */
export function useRailSettings(organizationId: string | null): UseRailSettingsResult {
  const [cells, setCells] = useState<RailAvailabilityCell[]>([]);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED (an attempt was requested but has not settled) — no synchronous setState in the
  // effect. A scope change deliberately brings the skeletons back: the previous scope's matrix must not sit
  // on screen with live switches meanwhile, or a toggle would write an override for the wrong organization.
  // Only the manual refresh (isRefreshing) keeps the table up while it reloads.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const attemptKey = `${reloadKey}:${organizationId ?? ""}`;
  const isLoading = settledKey !== attemptKey && !isRefreshing;

  useEffect(() => {
    let cancelled = false;
    PaymentRailsService.list(organizationId)
      .then((response) => {
        if (cancelled) return;
        setCells(response.rails ?? []);
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(attemptKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attemptKey, organizationId]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const kpis = useMemo<RailKpis>(
    () => ({
      totalCells: cells.length,
      disabledCells: cells.filter((cell) => !cell.effective_enabled).length,
      cellsWithOverrides: cells.filter((cell) => cell.org_overrides.length > 0).length,
    }),
    [cells],
  );

  return { cells, kpis, isLoading, isRefreshing, hasError, hasAccess, refresh };
}
